import {
  FinancialConfig,
  FinancialCalculationResult,
  GatewayFeeModel,
  GatewayFeePayer,
  ActiveGateway
} from "../../types/financial";
import { ArenaCardMachine } from "../../types/userProfile";

export const DEFAULT_FINANCIAL_CONFIG: FinancialConfig = {
  platformCommissionRate: 4.0,
  operatorCommissionRate: 0.0,
  gatewayFeeModel: "FIXED",
  gatewayFeeFixed: 0.99,
  gatewayFeePercentage: 0.0,
  gatewayFeePayer: "ARENA",
  activeGateway: "ASAAS",
  pixAutoEnabled: true,
  allowManualPaymentConfirmInTest: true,
};

/**
 * Motor de Cálculo Financeiro Parametrizado.
 * Regra Financeira Definitiva:
 * - Preço integral do horário: courtPrice (definido pelo Dono da Arena)
 * - Entrada: entryPercentage (definido pelo Dono da Arena) -> entryAmount = courtPrice * entryPercentage
 * - Comissão da plataforma: platformCommission = courtPrice * platformCommissionRate (calculada sobre o PREÇO INTEGRAL)
 * - Repasse Arena: arenaNetAmount = entryAmount - platformCommission - asaasFee (descontada da entrada recebida)
 */
export function calculateFinancialSplit(
  grossAmountOrConfig: number | Partial<FinancialConfig>,
  config?: Partial<FinancialConfig>
): FinancialCalculationResult {
  let grossAmount = 0;
  let cfgInput: Partial<FinancialConfig> = {};

  if (typeof grossAmountOrConfig === "object" && grossAmountOrConfig !== null) {
    cfgInput = grossAmountOrConfig;
    grossAmount = Number(cfgInput.entryAmount ?? cfgInput.grossAmount ?? 0);
  } else {
    grossAmount = Number(grossAmountOrConfig) || 0;
    cfgInput = config || {};
  }

  const safeGross = Math.max(0, Number(grossAmount) || 0);
  const cfg: FinancialConfig = { ...DEFAULT_FINANCIAL_CONFIG, ...cfgInput };

  // Identificação de slots múltiplos
  const rawTimeSlot = (cfg as any).timeSlot || (cfg as any).time || "";
  let slotsCount = 1;
  if (Array.isArray((cfg as any).targetSlotIds) && (cfg as any).targetSlotIds.length > 1) {
    slotsCount = (cfg as any).targetSlotIds.length;
  } else if (Array.isArray((cfg as any).slots) && (cfg as any).slots.length > 1) {
    slotsCount = (cfg as any).slots.length;
  } else if (Array.isArray((cfg as any).selectedTimes) && (cfg as any).selectedTimes.length > 1) {
    slotsCount = (cfg as any).selectedTimes.length;
  } else if (typeof rawTimeSlot === "string" && rawTimeSlot.includes(",")) {
    slotsCount = rawTimeSlot.split(",").map((s) => s.trim()).filter(Boolean).length;
  }

  let rawCourtPrice = cfg.courtPrice && Number(cfg.courtPrice) > 0
    ? Number(cfg.courtPrice)
    : (safeGross > 0 ? safeGross : 50);

  const hourlyPrice = Number((cfg as any).pricePerHour || (cfg as any).courtPricePerHour || 0);
  if (slotsCount > 1) {
    if (hourlyPrice > 0 && Math.abs(rawCourtPrice - hourlyPrice) < 0.01) {
      rawCourtPrice = Number((hourlyPrice * slotsCount).toFixed(2));
    } else if (rawCourtPrice < safeGross) {
      rawCourtPrice = Number((rawCourtPrice * slotsCount).toFixed(2));
    }
  }

  const courtPrice = rawCourtPrice;

  const entryPercentage = cfg.entryPercentage && Number(cfg.entryPercentage) > 0
    ? Number(cfg.entryPercentage)
    : (courtPrice > 0 && safeGross > 0 ? Number(((safeGross / courtPrice) * 100).toFixed(2)) : 100);

  const entryAmount = cfg.entryAmount && Number(cfg.entryAmount) > 0
    ? Number(cfg.entryAmount)
    : (safeGross > 0 ? safeGross : Number(((courtPrice * entryPercentage) / 100).toFixed(2)));

  const commissionRate = Math.max(
    0,
    Number(
      (cfg as any).platformFeePercentage ??
        cfg.platformCommissionRate ??
        (cfg as any).commissionRate ??
        4.0
    ) || 0
  );
  // A comissão é calculada sobre o PREÇO INTEGRAL DO HORÁRIO (courtPrice), e NÃO sobre a entrada:
  const platformCommission = Number(((courtPrice * commissionRate) / 100).toFixed(2));

  const operatorCommissionRate = Math.max(0, Number(cfg.operatorCommissionRate) || 0);
  const operatorCommission = Number(((courtPrice * operatorCommissionRate) / 100).toFixed(2));

  const model: GatewayFeeModel = cfg.gatewayFeeModel || "FIXED";
  const feeFixed = Math.max(0, Number(cfg.gatewayFeeFixed ?? 0.99));
  const feePercent = Math.max(0, Number(cfg.gatewayFeePercentage ?? 0.0));

  let gatewayFeeAmount = 0;
  if (cfg.asaasFee !== undefined && cfg.asaasFee !== null) {
    gatewayFeeAmount = Number(cfg.asaasFee);
  } else if (model === "FIXED") {
    gatewayFeeAmount = feeFixed;
  } else if (model === "PERCENTAGE") {
    gatewayFeeAmount = (entryAmount * feePercent) / 100;
  } else if (model === "BOTH") {
    gatewayFeeAmount = feeFixed + (entryAmount * feePercent) / 100;
  }
  gatewayFeeAmount = Number(gatewayFeeAmount.toFixed(2));

  const payer: GatewayFeePayer = cfg.gatewayFeePayer || "ARENA";
  let arenaNet = 0;
  let platformNet = 0;

  if (payer === "ARENA") {
    arenaNet = entryAmount - platformCommission - operatorCommission - gatewayFeeAmount;
    platformNet = platformCommission + operatorCommission;
  } else {
    // "PLATFORM" pays the gateway fee
    arenaNet = entryAmount - platformCommission - operatorCommission;
    platformNet = platformCommission + operatorCommission - gatewayFeeAmount;
  }

  arenaNet = Math.max(0, Number(arenaNet.toFixed(2)));
  platformNet = Number(platformNet.toFixed(2));

  const activeGateway: ActiveGateway = cfg.activeGateway || "ASAAS";

  return {
    grossAmount: Number(entryAmount.toFixed(2)),
    courtPrice: Number(courtPrice.toFixed(2)),
    entryPercentage: Number(entryPercentage.toFixed(2)),
    entryAmount: Number(entryAmount.toFixed(2)),
    commissionRate,
    commissionAmount: platformCommission,
    platformCommission,
    platformFee: platformCommission,
    operatorCommissionRate,
    operatorCommission,
    gatewayFeeModel: model,
    gatewayFeeFixed: feeFixed,
    gatewayFeePercentage: feePercent,
    gatewayFeeAmount,
    asaasFee: gatewayFeeAmount,
    gatewayFeePayer: payer,
    arenaNetAmount: arenaNet,
    arenaNet,
    platformNet,
    activeGateway,
  };
}

export interface CardFeeRateResult {
  isValid: boolean;
  ratePercent: number;
  error?: string;
}

/**
 * Função central e determinística para validação e obtenção da taxa de cartão.
 * Valida se a máquina existe, está ativa e possui a taxa configurada para a modalidade/parcela.
 * Não utiliza fallbacks silenciosos ou taxas inventadas.
 */
export function getCardFeeRate(
  machine: ArenaCardMachine | null | undefined,
  modality: "debito" | "credito_vista" | "credito_parcelado" | string,
  installments?: number
): CardFeeRateResult {
  if (!machine) {
    return {
      isValid: false,
      ratePercent: 0,
      error: "Nenhuma máquina de cartão está cadastrada ou selecionada para esta arena.",
    };
  }

  if (machine.active === false) {
    return {
      isValid: false,
      ratePercent: 0,
      error: `A máquina "${machine.name}" está inativa.`,
    };
  }

  if (modality === "pix" || modality === "pix_maquininha") {
    const pixRate =
      machine.pixFeePercent !== undefined &&
      machine.pixFeePercent !== null &&
      typeof machine.pixFeePercent === "number" &&
      !isNaN(machine.pixFeePercent)
        ? Math.max(0, machine.pixFeePercent)
        : 0;
    return { isValid: true, ratePercent: pixRate };
  }

  if (modality === "debito") {
    if (
      machine.debitFeePercent !== undefined &&
      machine.debitFeePercent !== null &&
      typeof machine.debitFeePercent === "number" &&
      !isNaN(machine.debitFeePercent) &&
      machine.debitFeePercent >= 0
    ) {
      return { isValid: true, ratePercent: machine.debitFeePercent };
    }
    return {
      isValid: false,
      ratePercent: 0,
      error: `Taxa de débito não configurada para a máquina "${machine.name}".`,
    };
  }

  if (modality === "credito_vista") {
    if (
      machine.creditSightFeePercent !== undefined &&
      machine.creditSightFeePercent !== null &&
      typeof machine.creditSightFeePercent === "number" &&
      !isNaN(machine.creditSightFeePercent) &&
      machine.creditSightFeePercent >= 0
    ) {
      return { isValid: true, ratePercent: machine.creditSightFeePercent };
    }
    return {
      isValid: false,
      ratePercent: 0,
      error: `Taxa de crédito à vista não configurada para a máquina "${machine.name}".`,
    };
  }

  if (modality === "credito_parcelado") {
    const inst = Math.max(1, Number(installments) || 2);
    const maxInst = machine.maxInstallments ? Number(machine.maxInstallments) : 12;
    if (inst > maxInst) {
      return {
        isValid: false,
        ratePercent: 0,
        error: `A máquina "${machine.name}" aceita no máximo ${maxInst} parcelas.`,
      };
    }

    const fee = machine.installmentFees
      ? (machine.installmentFees[inst] ?? machine.installmentFees[String(inst)])
      : undefined;

    if (
      fee !== undefined &&
      fee !== null &&
      typeof fee === "number" &&
      !isNaN(fee) &&
      fee >= 0
    ) {
      return { isValid: true, ratePercent: fee };
    }

    return {
      isValid: false,
      ratePercent: 0,
      error: `A taxa para ${inst}x não está configurada na máquina "${machine.name}".`,
    };
  }

  return {
    isValid: false,
    ratePercent: 0,
    error: `Modalidade "${modality}" não suportada.`,
  };
}

export interface CardSaleCalculationInput {
  amount: number;
  modality: "debito" | "credito_vista" | "credito_parcelado" | string;
  installments?: number;
  machine?: ArenaCardMachine | null;
  paymentMethod?: string;
}

export interface CardSaleCalculationResult {
  isValid: boolean;
  grossAmount: number;
  ratePercent: number;
  feeAmount: number;
  netAmount: number;
  machineName: string;
  machineId?: string;
  modality: string;
  installments: number;
  error?: string;
}

/**
 * Calcula a taxa da maquininha de cartão para vendas no PDV e Comandas.
 * Suporta Débito, Crédito à Vista e Crédito Parcelado de forma dinâmica.
 * Regras:
 * - Dinheiro: taxa R$ 0, líquido = bruto.
 * - PIX presencial: taxa da máquina R$ 0, líquido = bruto.
 * - Cartão: exige máquina ativa e taxa da modalidade configurada; caso contrário, retorna isValid=false com mensagem descritiva.
 * - Nunca aplica taxa de gateway online (R$ 0,99) a vendas de balcão no cartão.
 */
export function calculateCardSaleFee(
  amountOrInput: number | CardSaleCalculationInput,
  configOrModality?: any,
  installmentsParam?: number
): CardSaleCalculationResult {
  let gross = 0;
  let modality = "debito";
  let installments = 1;
  let machine: ArenaCardMachine | null | undefined = null;
  let paymentMethod = "cartao";

  if (typeof amountOrInput === "object" && amountOrInput !== null) {
    gross = Math.max(0, Number(amountOrInput.amount) || 0);
    modality = amountOrInput.modality || "debito";
    installments = amountOrInput.installments || 1;
    machine = amountOrInput.machine;
    paymentMethod = (amountOrInput.paymentMethod || "cartao").trim().toLowerCase();
  } else {
    gross = Math.max(0, Number(amountOrInput) || 0);
    if (typeof configOrModality === "object" && configOrModality !== null) {
      // Objeto de configuração direta (ex: { machineType: "STONE", feePercentage: 3.5, feeFixed: 0 })
      const feePercent = Number(configOrModality.feePercentage ?? configOrModality.ratePercent ?? 0);
      const fixedFee = Number(configOrModality.feeFixed ?? 0);
      const feeAmount = Number((((gross * feePercent) / 100) + fixedFee).toFixed(2));
      const netAmount = Number(Math.max(0, gross - feeAmount).toFixed(2));

      return {
        isValid: true,
        grossAmount: gross,
        ratePercent: feePercent,
        feeAmount,
        netAmount,
        machineName: configOrModality.machineType || configOrModality.name || "Maquininha",
        machineId: configOrModality.id,
        modality: configOrModality.modality || "cartao",
        installments: 1,
      };
    } else if (typeof configOrModality === "string") {
      modality = configOrModality;
      installments = installmentsParam || 1;
    }
  }

  if (paymentMethod === "dinheiro") {
    return {
      isValid: true,
      grossAmount: gross,
      ratePercent: 0,
      feeAmount: 0,
      netAmount: gross,
      machineName: "",
      modality: "dinheiro",
      installments: 1,
    };
  }

  // PIX DIRETO DA ARENA (pix_balcao) OU PIX SEM MÁQUINA
  if (paymentMethod === "pix_balcao" || (paymentMethod === "pix" && !machine)) {
    return {
      isValid: true,
      grossAmount: gross,
      ratePercent: 0,
      feeAmount: 0,
      netAmount: gross,
      machineName: "",
      modality: paymentMethod,
      installments: 1,
    };
  }

  // PIX VIA MAQUININHA (pix_maquininha OU pix com máquina)
  if (paymentMethod === "pix_maquininha" || (paymentMethod === "pix" && machine)) {
    if (!machine) {
      return {
        isValid: false,
        grossAmount: gross,
        ratePercent: 0,
        feeAmount: 0,
        netAmount: gross,
        machineName: "",
        modality: paymentMethod,
        installments: 1,
        error: "Nenhuma máquina de cartão está cadastrada ou selecionada para esta arena.",
      };
    }

    if (machine.active === false) {
      return {
        isValid: false,
        grossAmount: gross,
        ratePercent: 0,
        feeAmount: 0,
        netAmount: gross,
        machineName: machine.name || "",
        machineId: machine.id,
        modality: paymentMethod,
        installments: 1,
        error: `A máquina "${machine.name}" está inativa.`,
      };
    }

    const pixRate =
      machine.pixFeePercent !== undefined &&
      machine.pixFeePercent !== null &&
      typeof machine.pixFeePercent === "number" &&
      !isNaN(machine.pixFeePercent)
        ? Math.max(0, machine.pixFeePercent)
        : 0;

    const feeAmount = Number(((gross * pixRate) / 100).toFixed(2));
    const netAmount = Number(Math.max(0, gross - feeAmount).toFixed(2));

    return {
      isValid: true,
      grossAmount: gross,
      ratePercent: pixRate,
      feeAmount,
      netAmount,
      machineName: machine.name || "",
      machineId: machine.id,
      modality: paymentMethod,
      installments: 1,
    };
  }

  const normalizedModality = modality || "debito";
  const normalizedInstallments = normalizedModality === "credito_parcelado" ? Math.max(1, Number(installments) || 2) : 1;

  const rateValidation = getCardFeeRate(machine, normalizedModality, normalizedInstallments);

  if (!rateValidation.isValid) {
    return {
      isValid: false,
      grossAmount: gross,
      ratePercent: 0,
      feeAmount: 0,
      netAmount: gross,
      machineName: machine?.name || "",
      machineId: machine?.id,
      modality: normalizedModality,
      installments: normalizedInstallments,
      error: rateValidation.error,
    };
  }

  const fixedFee = Math.max(0, Number(machine?.fixedFeePerTransaction) || 0);
  const feeAmount = Number((((gross * rateValidation.ratePercent) / 100) + fixedFee).toFixed(2));
  const netAmount = Number(Math.max(0, gross - feeAmount).toFixed(2));

  return {
    isValid: true,
    grossAmount: gross,
    ratePercent: rateValidation.ratePercent,
    feeAmount,
    netAmount,
    machineName: machine?.name || "",
    machineId: machine?.id,
    modality: normalizedModality,
    installments: normalizedInstallments,
  };
}

export interface PresentialPaymentCalculationInput {
  amount: number;
  paymentMethod: string;
  machine?: ArenaCardMachine | null;
}

export interface PresentialPaymentCalculationResult {
  isValid: boolean;
  grossAmount: number;
  ratePercent: number;
  feeAmount: number;
  netAmount: number;
  machineName: string;
  machineId?: string;
  error?: string;
}

/**
 * Função central e determinística para cálculo de recebimento presencial (Check-in / Balcão).
 * Garante que PIX Direto (taxa 0%) e PIX via Maquininha (com taxa percentual da máquina)
 * sejam apurados de forma única em todo o sistema.
 */
export function calculatePresentialPaymentFee(
  input: PresentialPaymentCalculationInput
): PresentialPaymentCalculationResult {
  const { amount, paymentMethod, machine } = input;
  const gross = Math.max(0, Number(amount) || 0);

  if (paymentMethod === "pix_balcao" || paymentMethod === "dinheiro" || paymentMethod === "outros") {
    return {
      isValid: true,
      grossAmount: gross,
      ratePercent: 0,
      feeAmount: 0,
      netAmount: gross,
      machineName: "",
      machineId: undefined,
    };
  }

  if (paymentMethod === "pix_maquininha" || paymentMethod === "pix") {
    if (!machine) {
      if (paymentMethod === "pix") {
        return {
          isValid: true,
          grossAmount: gross,
          ratePercent: 0,
          feeAmount: 0,
          netAmount: gross,
          machineName: "",
          machineId: undefined,
        };
      }
      return {
        isValid: false,
        grossAmount: gross,
        ratePercent: 0,
        feeAmount: 0,
        netAmount: gross,
        machineName: "",
        error: "Nenhuma máquina de cartão está cadastrada ou selecionada para esta arena.",
      };
    }

    if (machine.active === false) {
      return {
        isValid: false,
        grossAmount: gross,
        ratePercent: 0,
        feeAmount: 0,
        netAmount: gross,
        machineName: machine.name || "",
        machineId: machine.id,
        error: `A máquina "${machine.name}" está inativa.`,
      };
    }

    const pixRate =
      machine.pixFeePercent !== undefined &&
      machine.pixFeePercent !== null &&
      typeof machine.pixFeePercent === "number" &&
      !isNaN(machine.pixFeePercent)
        ? Math.max(0, machine.pixFeePercent)
        : 0;

    const feeAmount = Number(((gross * pixRate) / 100).toFixed(2));
    const netAmount = Number(Math.max(0, gross - feeAmount).toFixed(2));

    return {
      isValid: true,
      grossAmount: gross,
      ratePercent: pixRate,
      feeAmount,
      netAmount,
      machineName: machine.name || "",
      machineId: machine.id,
    };
  }

  if (paymentMethod === "cartao_debito_balcao") {
    const res = calculateCardSaleFee({
      amount: gross,
      modality: "debito",
      machine,
      paymentMethod: "cartao",
    });
    return {
      isValid: res.isValid,
      grossAmount: res.grossAmount,
      ratePercent: res.ratePercent,
      feeAmount: res.feeAmount,
      netAmount: res.netAmount,
      machineName: res.machineName,
      machineId: res.machineId,
      error: res.error,
    };
  }

  if (paymentMethod === "cartao_credito_balcao") {
    const res = calculateCardSaleFee({
      amount: gross,
      modality: "credito_vista",
      machine,
      paymentMethod: "cartao",
    });
    return {
      isValid: res.isValid,
      grossAmount: res.grossAmount,
      ratePercent: res.ratePercent,
      feeAmount: res.feeAmount,
      netAmount: res.netAmount,
      machineName: res.machineName,
      machineId: res.machineId,
      error: res.error,
    };
  }

  return {
    isValid: true,
    grossAmount: gross,
    ratePercent: 0,
    feeAmount: 0,
    netAmount: gross,
    machineName: "",
    machineId: undefined,
  };
}

export const FinancialCalculator = {
  calculateFinancialSplit,
  getCardFeeRate,
  calculateCardSaleFee,
  calculatePresentialPaymentFee,
  DEFAULT_FINANCIAL_CONFIG,
};
